import { z } from '@harvestiq/zod';
import Decimal from 'decimal.js';
import { CommodityId } from './CommodityId';
import {
  RmaLrpCommodityCode,
  RmaLrpCommodityCodes,
  RmaLrpCommodityNames,
} from './lrp/LrpCommodities';
import {
  RmaLgmCommodityCode,
  RmaLgmCommodityCodes,
  RmaLgmCommodityNames,
} from './lgm/LgmCommodities';
import { UnitOfMeasure } from './UOM';
import { zDecimal } from '@harvestiq/zod';

export const EXCHANGES = {
  CBOT: 'CBOT',
  KCBT: 'KCBT',
  MGEX: 'MIAX',
  CME: 'CME',
} as const;

export const EXCHANGES_FOR_REQUESTS = 'CBOT,MIAX,KCBT,CME'; // Tests expect this order

export const CommodityInformation = z.object({
  id: z.number().int().gte(0),
  name: z.string(),
  tradingCode: z.string(),
  barchartExchangeCode: z.string(),
  specialOptionTradingCode: z.string().optional(),
  miniTradingCode: z.string().optional(),
  uom: z.nativeEnum(UnitOfMeasure),
  standardSize: zDecimal(),
  miniSize: zDecimal().optional(),
  futuresTradingMonths: z.array(z.number().int().positive()),
  newCropMonth: z.number().int().positive(),
  shortDatedCutoff: z.number().int().positive(),

  cqgTradingCode: z.string().optional(),
  // all known short-dated trading codes are the same as the regular trading code with a 'D' appended
  cqgShortDatedTradingCode: z.string().optional(),
  cqgMiniTradingCode: z.string().optional(),
  cqgStrikeMultiplier: z.number().int().optional(),
  optionStrikePriceStep: zDecimal().optional(),
  shortDatedAndWeeklyOptionsStrikePrice: zDecimal().optional(),
  nearTermMonthDistance: z.number().optional(),
  nearTermOptionsStrikePrice: zDecimal().optional(),
  hasSpecialOptions: z.boolean().optional(),
  barchartStrikeMultiplier: z.number().int(),
  barchartPriceFactor: z.number().int(),
  barchartOptionsPriceFactor: z.number().int().optional(), // used for options, defaults to barchartPriceFactor in code

  formatPriceMaxDigitsAfterDecimal: z.number().int(),
  formatOptionsMaxDigitsAfterDecimal: z.number().int(),

  sgLrpCommodity: z.nativeEnum(RmaLrpCommodityNames).optional(),
  sgLgmCommodity: z.nativeEnum(RmaLgmCommodityNames).optional(),
  isLivestock: z.boolean(),
  isTradeable: z.boolean(), // can be traded in the application and can track hedging positions
  isMarketingCrop: z.boolean(), // can be used for marketing crop and is priced in futures and basis
  isHedgingCrop: z.boolean(), // can be used for hedging crop, we have market data for it
  // TODO isTradeable and isHedgingCrop are munged together
  //   isTradeable should just be if it can be traded in the application
  //   isHedgingCrop should be if we have market data for it and we can track hedging positions
});
export type CommodityInformation = z.infer<typeof CommodityInformation>;

export const COMMODITIES: Record<CommodityId, CommodityInformation> = {
  [CommodityId.UNKNOWN]: {
    id: 0,
    name: 'Other/None',
    tradingCode: '',
    barchartExchangeCode: '',
    uom: UnitOfMeasure.Bushel,
    standardSize: new Decimal(0),
    futuresTradingMonths: [],
    newCropMonth: 0,
    shortDatedCutoff: 0,
    barchartStrikeMultiplier: 0,
    barchartPriceFactor: 100, // price in cents
    formatPriceMaxDigitsAfterDecimal: 2,
    formatOptionsMaxDigitsAfterDecimal: 2,
    isLivestock: false,
    isTradeable: false,
    isMarketingCrop: true,
    isHedgingCrop: true,
  },
  [CommodityId.CORN]: {
    id: CommodityId.CORN,
    name: 'Corn',
    tradingCode: 'ZC',
    barchartExchangeCode: 'CBOT',
    specialOptionTradingCode: 'BC',
    miniTradingCode: 'XN',
    cqgTradingCode: 'ZCE',
    cqgShortDatedTradingCode: 'ZCED',
    cqgMiniTradingCode: 'XC',
    optionStrikePriceStep: new Decimal(0.1),
    shortDatedAndWeeklyOptionsStrikePrice: new Decimal(0.05),
    nearTermMonthDistance: 3,
    nearTermOptionsStrikePrice: new Decimal(0.05),
    hasSpecialOptions: true,
    uom: UnitOfMeasure.Bushel,
    standardSize: new Decimal(5000),
    miniSize: new Decimal(1000),
    futuresTradingMonths: [2, 4, 6, 8, 11],
    newCropMonth: 11,
    shortDatedCutoff: 8,
    cqgStrikeMultiplier: 1000,
    barchartStrikeMultiplier: 100,
    barchartPriceFactor: 100, // price in cents
    formatPriceMaxDigitsAfterDecimal: 4, // 1/4  cents
    formatOptionsMaxDigitsAfterDecimal: 5, // 1/8 cents
    isLivestock: false,
    isTradeable: true,
    isMarketingCrop: true,
    isHedgingCrop: true,
  },
  [CommodityId.SOYBEANS]: {
    id: CommodityId.SOYBEANS,
    name: 'Soybeans',
    tradingCode: 'ZS',
    barchartExchangeCode: 'CBOT',
    specialOptionTradingCode: 'BY',
    miniTradingCode: 'XK',
    cqgTradingCode: 'ZSE',
    cqgShortDatedTradingCode: 'ZSED',
    cqgMiniTradingCode: 'XB',
    optionStrikePriceStep: new Decimal(0.2),
    shortDatedAndWeeklyOptionsStrikePrice: new Decimal(0.1),
    nearTermMonthDistance: 3,
    nearTermOptionsStrikePrice: new Decimal(0.1),
    hasSpecialOptions: true,
    uom: UnitOfMeasure.Bushel,
    standardSize: new Decimal(5000),
    miniSize: new Decimal(1000),
    futuresTradingMonths: [0, 2, 4, 6, 7, 8, 10],
    newCropMonth: 10,
    shortDatedCutoff: 8,
    cqgStrikeMultiplier: 1000,
    barchartStrikeMultiplier: 100,
    barchartPriceFactor: 100, // price in cents
    formatPriceMaxDigitsAfterDecimal: 4, // 1/4  cents
    formatOptionsMaxDigitsAfterDecimal: 5, // 1/8 cents
    isLivestock: false,
    isTradeable: true,
    isMarketingCrop: true,
    isHedgingCrop: true,
  },
  [CommodityId.KC_WINTER_WHEAT_HRW]: {
    id: CommodityId.KC_WINTER_WHEAT_HRW,
    name: 'KC Winter Wheat HRW',
    tradingCode: 'KE',
    barchartExchangeCode: 'KCBT',
    specialOptionTradingCode: 'BK',
    // miniTradingCode: 'MKC', Can't find this in Barchart
    cqgTradingCode: 'KWE',
    cqgShortDatedTradingCode: 'KWED',
    cqgMiniTradingCode: 'MKC',
    optionStrikePriceStep: new Decimal(0.1),
    shortDatedAndWeeklyOptionsStrikePrice: new Decimal(0.05),
    nearTermMonthDistance: 3,
    nearTermOptionsStrikePrice: new Decimal(0.05),
    hasSpecialOptions: true,
    uom: UnitOfMeasure.Bushel,
    standardSize: new Decimal(5000),
    miniSize: new Decimal(1000),
    futuresTradingMonths: [2, 4, 6, 8, 11],
    newCropMonth: 6,
    shortDatedCutoff: 4,
    cqgStrikeMultiplier: 1000,
    barchartStrikeMultiplier: 100,
    barchartPriceFactor: 100, // price in cents
    formatPriceMaxDigitsAfterDecimal: 4, // 1/4  cents
    formatOptionsMaxDigitsAfterDecimal: 5, // 1/8 cents
    isLivestock: false,
    isTradeable: true,
    isMarketingCrop: true,
    isHedgingCrop: true,
  },
  [CommodityId.MN_SPRING_WHEAT_HRSW]: {
    id: CommodityId.MN_SPRING_WHEAT_HRSW,
    name: 'MN Spring Wheat HRSW',
    tradingCode: 'MW',
    barchartExchangeCode: 'MIAX',
    cqgTradingCode: 'MWE',
    optionStrikePriceStep: new Decimal(0.1),
    hasSpecialOptions: false,
    uom: UnitOfMeasure.Bushel,
    standardSize: new Decimal(5000),
    futuresTradingMonths: [2, 4, 6, 8, 11],
    newCropMonth: 8,
    shortDatedCutoff: 6,
    cqgStrikeMultiplier: 1000,
    barchartStrikeMultiplier: 100,
    barchartPriceFactor: 1, // price in dollars
    barchartOptionsPriceFactor: 100, // price in cents
    formatPriceMaxDigitsAfterDecimal: 4, // 1/4  cents
    formatOptionsMaxDigitsAfterDecimal: 5, // 1/8 cents
    isLivestock: false,
    isTradeable: true,
    isMarketingCrop: true,
    isHedgingCrop: true,
  },
  [CommodityId.CHI_SOFT_RED_WINTER_SRW]: {
    id: CommodityId.CHI_SOFT_RED_WINTER_SRW,
    name: 'CHI Winter Wheat SRW',
    tradingCode: 'ZW',
    barchartExchangeCode: 'CBOT',
    specialOptionTradingCode: 'BH',
    miniTradingCode: 'XW',
    cqgTradingCode: 'ZWA',
    cqgShortDatedTradingCode: 'ZWAD',
    cqgMiniTradingCode: 'XW',
    optionStrikePriceStep: new Decimal(0.1),
    shortDatedAndWeeklyOptionsStrikePrice: new Decimal(0.05),
    nearTermMonthDistance: 3,
    nearTermOptionsStrikePrice: new Decimal(0.05),
    hasSpecialOptions: true,
    uom: UnitOfMeasure.Bushel,
    standardSize: new Decimal(5000),
    miniSize: new Decimal(1000),
    futuresTradingMonths: [2, 4, 6, 8, 11],
    newCropMonth: 6,
    shortDatedCutoff: 4,
    cqgStrikeMultiplier: 1000,
    barchartStrikeMultiplier: 100,
    barchartPriceFactor: 100, // price in cents
    formatPriceMaxDigitsAfterDecimal: 4, // 1/4  cents
    formatOptionsMaxDigitsAfterDecimal: 5, // 1/8 cents
    isLivestock: false,
    isTradeable: true,
    isMarketingCrop: true,
    isHedgingCrop: true,
  },
  [CommodityId.OATS]: {
    id: CommodityId.OATS,
    name: 'Oats',
    tradingCode: 'ZO',
    barchartExchangeCode: 'CBOT',
    cqgTradingCode: 'ZOE',
    optionStrikePriceStep: new Decimal(0.05),
    nearTermMonthDistance: 2,
    nearTermOptionsStrikePrice: new Decimal(0.05),
    hasSpecialOptions: false,
    uom: UnitOfMeasure.Bushel,
    standardSize: new Decimal(5000),
    futuresTradingMonths: [2, 4, 6, 8, 11],
    newCropMonth: 8,
    shortDatedCutoff: 6,
    cqgStrikeMultiplier: 1000,
    barchartStrikeMultiplier: 100,
    barchartPriceFactor: 100, // price in cents
    formatPriceMaxDigitsAfterDecimal: 4, // 1/4  cents
    formatOptionsMaxDigitsAfterDecimal: 5, // 1/8 cents
    isLivestock: false,
    isTradeable: true,
    isMarketingCrop: true,
    isHedgingCrop: true,
  },
  [CommodityId.SORGHUM]: {
    id: CommodityId.SORGHUM,
    name: 'Sorghum',
    tradingCode: 'ZC',
    barchartExchangeCode: 'CBOT',
    specialOptionTradingCode: 'BC',
    cqgTradingCode: 'ZCE',
    cqgShortDatedTradingCode: 'ZCED',
    optionStrikePriceStep: new Decimal(0.05),
    nearTermMonthDistance: 3,
    nearTermOptionsStrikePrice: new Decimal(0.05),
    hasSpecialOptions: true,
    uom: UnitOfMeasure.Bushel,
    standardSize: new Decimal(5000),
    futuresTradingMonths: [2, 4, 6, 8, 11],
    newCropMonth: 11,
    shortDatedCutoff: 8,
    cqgStrikeMultiplier: 1000,
    barchartStrikeMultiplier: 100,
    barchartPriceFactor: 100, // price in cents
    formatPriceMaxDigitsAfterDecimal: 4, // 1/4  cents
    formatOptionsMaxDigitsAfterDecimal: 5, // 1/8 cents
    isLivestock: false,
    isTradeable: false,
    isMarketingCrop: true,
    isHedgingCrop: false,
  },
  [CommodityId.ZUC]: {
    id: CommodityId.ZUC,
    name: 'ZUC',
    tradingCode: 'ZUC',
    barchartExchangeCode: '',
    // specialOptionTradingCode: 'BC',
    cqgTradingCode: 'ZUC',
    // cqgShortDatedTradingCode: 'ZCED',
    optionStrikePriceStep: new Decimal(0.05),
    nearTermMonthDistance: 3,
    nearTermOptionsStrikePrice: new Decimal(0.05),
    hasSpecialOptions: false,
    uom: UnitOfMeasure.Bushel,
    standardSize: new Decimal(5000),
    futuresTradingMonths: [2, 4, 6, 8, 11],
    newCropMonth: 11,
    shortDatedCutoff: 8,
    cqgStrikeMultiplier: 1000,
    barchartStrikeMultiplier: 100,
    barchartPriceFactor: 100, // price in cents
    formatPriceMaxDigitsAfterDecimal: 4, // 1/4  cents
    formatOptionsMaxDigitsAfterDecimal: 5, // 1/8 cents
    isLivestock: false,
    isTradeable: false,
    isMarketingCrop: false,
    isHedgingCrop: false,
  },
  [CommodityId.LEAN_HOGS]: {
    id: CommodityId.LEAN_HOGS,
    name: 'Lean Hogs',
    tradingCode: 'HE',
    barchartExchangeCode: 'CME',
    cqgTradingCode: 'HE',
    hasSpecialOptions: false,
    uom: UnitOfMeasure.Pounds,
    standardSize: new Decimal(40000),
    futuresTradingMonths: [1, 3, 4, 5, 6, 7, 9, 11],
    optionStrikePriceStep: new Decimal(0.000125),
    newCropMonth: 1,
    shortDatedCutoff: 11,
    cqgStrikeMultiplier: 10000,
    barchartStrikeMultiplier: 1000,
    barchartPriceFactor: 100, // price in cents
    formatPriceMaxDigitsAfterDecimal: 5, // .025  cents
    formatOptionsMaxDigitsAfterDecimal: 6, // .0125 cents
    sgLrpCommodity: RmaLrpCommodityNames.SWINE,
    sgLgmCommodity: RmaLgmCommodityNames.SWINE,
    isLivestock: true,
    isTradeable: true,
    isMarketingCrop: false,
    isHedgingCrop: false,
  },
  [CommodityId.FEEDER_CATTLE]: {
    id: CommodityId.FEEDER_CATTLE,
    name: 'Feeder Cattle',
    tradingCode: 'GF',
    barchartExchangeCode: 'CME',
    cqgTradingCode: 'GF',
    hasSpecialOptions: false,
    uom: UnitOfMeasure.Pounds,
    standardSize: new Decimal(50000),
    futuresTradingMonths: [0, 2, 3, 4, 7, 8, 9, 10],
    optionStrikePriceStep: new Decimal(0.000125),
    newCropMonth: 0,
    shortDatedCutoff: 10,
    cqgStrikeMultiplier: 10000,
    barchartStrikeMultiplier: 1000,
    barchartPriceFactor: 100, // price in cents
    formatPriceMaxDigitsAfterDecimal: 5, // .025  cents
    formatOptionsMaxDigitsAfterDecimal: 6, // .0125 cents
    sgLrpCommodity: RmaLrpCommodityNames.FEEDER_CATTLE,
    isLivestock: true,
    isTradeable: true,
    isMarketingCrop: false,
    isHedgingCrop: false,
  },
  [CommodityId.LIVE_CATTLE]: {
    id: CommodityId.LIVE_CATTLE,
    name: 'Live Cattle',
    tradingCode: 'LE',
    barchartExchangeCode: 'CME',
    cqgTradingCode: 'GLE',
    hasSpecialOptions: false,
    uom: UnitOfMeasure.Pounds,
    standardSize: new Decimal(40000),
    futuresTradingMonths: [1, 3, 5, 7, 9, 11],
    optionStrikePriceStep: new Decimal(0.00025),
    newCropMonth: 1,
    shortDatedCutoff: 11,
    cqgStrikeMultiplier: 10000,
    barchartStrikeMultiplier: 1000,
    barchartPriceFactor: 100, // price in cents
    formatPriceMaxDigitsAfterDecimal: 5, // .025  cents
    formatOptionsMaxDigitsAfterDecimal: 6, // .0125 cents
    sgLrpCommodity: RmaLrpCommodityNames.FED_CATTLE,
    sgLgmCommodity: RmaLgmCommodityNames.CATTLE,
    isLivestock: true,
    isTradeable: true,
    isMarketingCrop: false,
    isHedgingCrop: false,
  },
  [CommodityId.SOYBEAN_MEAL]: {
    id: CommodityId.SOYBEAN_MEAL,
    name: 'Soybean Meal',
    tradingCode: 'ZM',
    barchartExchangeCode: 'CBOT',
    specialOptionTradingCode: 'BM',
    cqgTradingCode: 'ZME',
    cqgShortDatedTradingCode: 'ZMED',
    hasSpecialOptions: true,
    uom: UnitOfMeasure.ShortTon,
    standardSize: new Decimal(100),
    futuresTradingMonths: [0, 2, 4, 6, 8, 9, 11],
    optionStrikePriceStep: new Decimal(0.05),
    newCropMonth: 11,
    shortDatedCutoff: 9,
    cqgStrikeMultiplier: 10, // dollars not cents
    barchartStrikeMultiplier: 1,
    barchartPriceFactor: 1, // price in dollars
    formatPriceMaxDigitsAfterDecimal: 2, // .10  cents
    formatOptionsMaxDigitsAfterDecimal: 2, // .05 cents
    isLivestock: false,
    isTradeable: true,
    isMarketingCrop: false,
    isHedgingCrop: false,
  },
  [CommodityId.CANOLA]: {
    id: CommodityId.CANOLA,
    name: 'Canola',
    tradingCode: 'RS',
    barchartExchangeCode: 'ICE',
    // hasSpecialOptions: false, // has serial options
    // uom: UnitOfMeasure.MetricTon, // actual unit traded
    uom: UnitOfMeasure.Pounds, // unit used with US producers
    // standardSize: new Decimal(20), // actual unit traded
    standardSize: new Decimal(2204.62), // unit used with US producers -ish
    futuresTradingMonths: [0, 2, 4, 6, 10],
    optionStrikePriceStep: new Decimal(0.05),
    newCropMonth: 0,
    shortDatedCutoff: 10,
    barchartPriceFactor: 1, // not verified
    barchartStrikeMultiplier: 1, // not verified
    formatPriceMaxDigitsAfterDecimal: 2, // 1.0 cent CAD
    formatOptionsMaxDigitsAfterDecimal: 2, // 0.1 cents CAD
    isLivestock: false,
    isTradeable: false,
    isMarketingCrop: true,
    isHedgingCrop: false,
  },
  [CommodityId.MILK_3]: {
    id: CommodityId.MILK_3,
    name: 'Milk 3',
    tradingCode: 'DL',
    barchartExchangeCode: 'CME',
    cqgTradingCode: 'GDC',
    hasSpecialOptions: false,
    uom: UnitOfMeasure.HundredWeight,
    standardSize: new Decimal(2000),
    futuresTradingMonths: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    optionStrikePriceStep: new Decimal(0.01),
    newCropMonth: 0,
    shortDatedCutoff: 10,
    cqgStrikeMultiplier: 100,
    barchartStrikeMultiplier: 100,
    barchartPriceFactor: 1, // price in dollars
    formatPriceMaxDigitsAfterDecimal: 2, // .01 dollars
    formatOptionsMaxDigitsAfterDecimal: 2, // .01 dollars
    isLivestock: false,
    isTradeable: true,
    isMarketingCrop: false,
    isHedgingCrop: true,
  },
  [CommodityId.MILK_4]: {
    id: CommodityId.MILK_4,
    name: 'Milk 4',
    tradingCode: 'DK',
    barchartExchangeCode: 'CME',
    cqgTradingCode: 'GDK',
    hasSpecialOptions: false,
    uom: UnitOfMeasure.HundredWeight,
    standardSize: new Decimal(2000),
    futuresTradingMonths: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    optionStrikePriceStep: new Decimal(0.01),
    newCropMonth: 0,
    shortDatedCutoff: 10,
    cqgStrikeMultiplier: 100,
    barchartStrikeMultiplier: 100,
    barchartPriceFactor: 1, // price in dollars
    formatPriceMaxDigitsAfterDecimal: 2, // .01 dollars
    formatOptionsMaxDigitsAfterDecimal: 2, // .01 dollars
    isLivestock: false,
    isTradeable: true,
    isMarketingCrop: false,
    isHedgingCrop: true,
  },
};

export const insureiqLgmCommodities: [string, ...string[]] = [
  COMMODITIES[CommodityId.CORN].name,
  COMMODITIES[CommodityId.SOYBEAN_MEAL].name,
  COMMODITIES[CommodityId.LIVE_CATTLE].name,
  COMMODITIES[CommodityId.FEEDER_CATTLE].name,
  'Lean Hog', // doesn't end in 's' COMMODITIES[CommodityId.LEAN_HOGS].name
  'Expected Gross Margin',
];

export type LrpLivestockCommodityId =
  | CommodityId.FEEDER_CATTLE
  | CommodityId.LIVE_CATTLE
  | CommodityId.LEAN_HOGS;

export const LrpCommodityNamesByCommodityId: Record<
  LrpLivestockCommodityId,
  RmaLrpCommodityNames
> = {
  [CommodityId.FEEDER_CATTLE]: RmaLrpCommodityNames.FEEDER_CATTLE,
  [CommodityId.LIVE_CATTLE]: RmaLrpCommodityNames.FED_CATTLE,
  [CommodityId.LEAN_HOGS]: RmaLrpCommodityNames.SWINE,
};

export function hasTradingCode(
  object: CommodityInformation
): object is CommodityInformation {
  // filter off OTHER/NONE
  if ('tradingCode' in object && object['tradingCode']) {
    return true;
  }
  return false;
}

export const CROP_COMMODITIES: CommodityInformation[] = Object.values(
  COMMODITIES
).filter(({ isLivestock }) => !isLivestock);

export const LIVESTOCK_COMMODITIES: CommodityInformation[] = Object.values(
  COMMODITIES
).filter(({ isLivestock }) => isLivestock);

export const TRADEABLE_COMMODITIES: CommodityInformation[] = Object.values(
  COMMODITIES
).filter(({ isTradeable }) => isTradeable);

export const HEDGING_CROPS: CommodityInformation[] = Object.values(
  COMMODITIES
).filter(({ isHedgingCrop }) => isHedgingCrop);

export const MARKETING_CROPS: CommodityInformation[] = Object.values(
  COMMODITIES
).filter(({ isMarketingCrop }) => isMarketingCrop);

export enum CommodityGroup {
  ALL = 'All',
  CROPS = 'Crops',
  LIVESTOCK = 'Livestock',
}
export function getCommodities(
  includeTestingCrops?: boolean,
  type: CommodityGroup = CommodityGroup.ALL
): CommodityInformation[] {
  let commodities = Object.values(COMMODITIES);

  if (type === CommodityGroup.CROPS) {
    commodities = CROP_COMMODITIES;
  }

  if (type === CommodityGroup.LIVESTOCK) {
    commodities = LIVESTOCK_COMMODITIES;
  }

  if (includeTestingCrops) {
    return commodities;
  }

  // Filter off ZUC in prod
  return commodities.filter((commodity) => commodity.id !== CommodityId.ZUC);
}

export const isLivestockCommodity = (commodityId: CommodityId): boolean =>
  LIVESTOCK_COMMODITIES.some((commodity) => commodity.id === commodityId);

export function getTradeableCommodities(): CommodityInformation[] {
  return TRADEABLE_COMMODITIES;
}

export function getCommodityByCommodityId(
  commodityId: CommodityId
): CommodityInformation {
  return COMMODITIES[commodityId] ?? COMMODITIES[CommodityId.UNKNOWN];
}

export function getCommodityByTradingCode(
  tradingCode: string
): CommodityInformation | undefined {
  const upperCode = tradingCode.toUpperCase();
  return Object.values(TRADEABLE_COMMODITIES).find((crop) => {
    return crop.tradingCode === upperCode || crop.miniTradingCode === upperCode;
  });
}

export function isTradingCodeMini(tradingCode: string): boolean {
  const upperCode = tradingCode.toUpperCase();
  return Object.values(TRADEABLE_COMMODITIES).some(
    (crop) => crop.miniTradingCode === upperCode
  );
}

export function getCommodityInfoFromRmaLrpCommodityCode(
  commodityCode: RmaLrpCommodityCode
) {
  switch (commodityCode) {
    case RmaLrpCommodityCodes.FEEDER_CATTLE:
      return COMMODITIES[CommodityId.FEEDER_CATTLE];
    case RmaLrpCommodityCodes.FED_CATTLE:
      return COMMODITIES[CommodityId.LIVE_CATTLE];
    case RmaLrpCommodityCodes.SWINE:
      return COMMODITIES[CommodityId.LEAN_HOGS];

    default:
      throw new Error(`Unknown RMA LRP commodity code: ${commodityCode}`);
  }
}

export function getCommodityInfoFromRmaLgmCommodityCode(
  commodityCode: RmaLgmCommodityCode
) {
  switch (commodityCode) {
    case RmaLgmCommodityCodes.CATTLE:
      return COMMODITIES[CommodityId.LIVE_CATTLE];
    case RmaLgmCommodityCodes.SWINE:
      return COMMODITIES[CommodityId.LEAN_HOGS];

    default:
      throw new Error(`Unknown RMA LGM commodity code: ${commodityCode}`);
  }
}

export const getLivestockCommodityName = (
  targetCommodityId: CommodityId
): string =>
  LrpCommodityNamesByCommodityId[
    targetCommodityId as LrpLivestockCommodityId
  ] || '';
